import React, {useState, useEffect, useContext, useMemo} from 'react';
import { colors, layout, shadows } from '../../theme';
import Fetch from '../../utils/Fetch';
import DownloadService from '../../utils/download-service';
import Time from '../../utils/time';
import Helper from '../../utils/helper';
import * as Enums from '../../utils/enums';
import QuoteDetail from '../../components/customer-zone/quote/quote-detail';
import QuoteItems from '../../components/customer-zone/quote/quote-items';
import QuoteTotals from '../../components/customer-zone/quote/quote-totals';
import MessagePost from '../../components/modals/message-post';
import Button from '../../components/button';
import ToastContext from '../../utils/toast-context';
import ConfirmAction from '../../components/modals/confirm-action';
import PageContext from '../../utils/page-context';
import NoSSR from '../../utils/no-ssr'
import useMobileView from '../../hooks/useMobileView';
import PaymentService from '../../services/payment-service';
import { roundPrice } from '../../utils/line-totals';
import CompanyService from '../../services/company-service';
import LeadMessage from '../../components/customer-zone/general/lead-message';
import {useQuery} from "@tanstack/react-query";
import {getCustomerZoneItemPayments} from "@/PageComponents/Payments/payments";
import PaymentsTable from "@/PageComponents/CustomerZone/PaymentsTable";
import { Flex, Tooltip, Button as MantineButton, Menu } from "@mantine/core";
import { IconCaretDownFilled, IconBrandWhatsapp } from '@tabler/icons-react';
import { useRouter } from 'next/router';
import { createFileRoute } from '@tanstack/react-router'

function ViewQuote() {

  const router = useRouter();
  const { t, c, i, s, com } = router.query as any;

  const toast = useContext<any>(ToastContext);
  const pageContext = useContext<any>(PageContext);

  const [mobileView] = useMobileView();

  const tenantQuery = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t } as any),
    { enabled: !!t, onError: console.error }
  );

  const tenant = (tenantQuery.data?.HttpStatusCode === 200 && tenantQuery.data?.Results?.length > 0)
    ? tenantQuery.data.Results[0]
    : null;

  const redirect = (tenantQuery.isSuccess && !tenant)
    ? `/customerzone/quotelist?t=${t}&c=${c}`
    : null;

  const quoteQuery = useQuery(
    ['czQuote', t, c, i],
    () => Fetch.get({
      url: `/CustomerZone/GetQuote`,
      params: { quoteID: i },
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    }),
    { enabled: !!tenant, onError: console.error }
  );

  const companyQuery = useQuery(
    ['czCompany', t, c],
    () => CompanyService.getCustomerZoneCompany(t, c, tenant.API),
    { enabled: !!tenant, onError: console.error }
  );

  const [quote, setQuote] = useState<any>(null);

  useEffect(() => {
    if (quoteQuery.data) {
      setQuote(quoteQuery.data);
    }
  }, [quoteQuery.data]);

  useEffect(() => {
    if (redirect) {
      Helper.nextRouter(router.replace, redirect);
    }
  }, [redirect]);

  useEffect(() => {
    if (tenant && quoteQuery.data) {
      pageContext.updateTenantName(quoteQuery.data.headerTenantName);
    }
  }, [tenant, quoteQuery.data]);

  const command = com;
  const tenantID = t;
  const customerID = c;
  const apiUrl = tenant?.API;

  const company = companyQuery.data;
  const currencySymbol = company?.Currency?.Symbol || '';
  const taxPercentage = company?.TaxPercentage;

  const store = quoteQuery.data?.Store || null;
  const bankName = store ? (store.BankName || null) : (company?.BankName || null);
  const bankAccountNumber = store ? (store.BankAccountNumber || null) : (company?.BankAccountNumber || null);
  const bankBranchCode = store ? (store.BankBranchCode || null) : (company?.BankBranchCode || null);

  const primaryContact = (quote?.Contact ? quote.Contact : undefined);
  const quoteLocation = (quote?.Location ? quote.Location : undefined);

  const exportDocument = () => {
    DownloadService.downloadFile("GET", `/CustomerZone/GetQuoteDocument?quoteID=${quote.ID}`, null, true, false,
      tenantID, customerID, tenant.API, mobileView);
  };

  const downloadDocument = () => {
    DownloadService.downloadFile("GET", `/CustomerZone/GetQuoteDocument?quoteID=${quote.ID}`, null, false, false,
      tenantID, customerID, tenant.API, mobileView);
  };

  const itemPaymentsQuery = useQuery(['payments', 'customerZoneItemList', quote?.ID],
      () => getCustomerZoneItemPayments(quote.ID, Enums.Module.Quote, tenantID, customerID, apiUrl), {
        enabled: !!quote,
        onError: console.error
      });

  const handleWhatsAppClick = () => {
    if (!company?.WhatsAppNumber) return;

    const message = `Hi! This is ${quote.CustomerName} reaching out about quote ${quote.QuoteNumber}.`;
    let phone = company.WhatsAppNumber.replace(/\D/g, '');
    if (phone.startsWith('0')) {
      phone = '27' + phone.substring(1);
    }

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const [showMessagePost, setShowMessagePost] = useState(false);
  const onMessagePost = (state) => {
    setShowMessagePost(false);
  };

  const acceptDeclineQuoteClick = (status) => {
    if (status == Enums.QuoteStatus.Accepted) {
      confirmChangeStatus('accept', Enums.QuoteStatus.Accepted);
    } else if (status == Enums.QuoteStatus.Declined) {
      confirmChangeStatus('decline', Enums.QuoteStatus.Declined);
    }
  };

  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());

  const confirmChangeStatus = async (statusString, status) => {
    setConfirmOptions({
      display: true,
      heading: "Confirm",
      text: `Are you sure you want to ${statusString} this quote?`,
      confirmButtonText: "Confirm",
      showCancel: true,
      onConfirm: async () => {
        await updateQuoteStatus(status);
      }
    } as any);
  };

  const updateQuoteStatus = async (status) => {
    const put = await Fetch.put({
      url: `/CustomerZone/UpdateQuoteStatus?id=${quote.ID}&quoteStatus=${status}`,
      tenantID: tenantID,
      customerID: customerID,
      apiUrlOverride: tenant.API
    });
    if (put.ID) {
      setQuote(put);
      toast.setToast({
        message: 'Status updated successfully',
        show: true,
        type: 'success'
      });
    } else {
      toast.setToast({
        message: 'Error updating status',
        show: true,
        type: Enums.ToastType.error,
      });
    }
  };

  const [payNowLink, setPayNowLink] = useState(null);

  const getPayNowLink = async () => {
    if (!!redirect || !tenant || !quote) return;

    const payments = itemPaymentsQuery.data?.Results || [];
    const totalPaid = payments.reduce((sum: number, p: any) => sum + +p.Amount, 0);
    const amountDue = Math.max(0, roundPrice(quote.TotalInclusive - totalPaid));

    let url = await PaymentService.getPayNowLink(quote.ID, quote.QuoteNumber, Enums.Module.Quote, amountDue,
      tenantID, customerID, tenant.API);

    setPayNowLink(url);
  };

  const isExpired = useMemo(
      () => quote?.ExpiryDate ? Time.getTimeDifference(Time.today(), quote.ExpiryDate) < 0 : false,
      [quote]
  )

  useEffect(() => {
    getPayNowLink();
  }, [quote, tenant, itemPaymentsQuery.data]);

  const payNow = () => {
    if (!!payNowLink) window.location.href = payNowLink;
  }

  useEffect(() => {
    if (payNowLink && command == "paynow" && quote?.QuoteStatus === Enums.QuoteStatus.Accepted) {
      payNow();
    }
  }, [payNowLink]);

  const displayQuoteButtons = () => {
    return (<div className={"quote-buttons"}>

      {quote.QuoteStatus === Enums.QuoteStatus.Accepted && !!payNowLink ?
        <div className={mobileView ? "row" : ""}>
          <Tooltip
              label={`Please contact ${quote?.headerTenantName || 'us'} for additional assistance`}
              disabled={!isExpired}
              events={{ hover: true, focus: true, touch: true }}
          >
            <div>
              <div className={mobileView ? "center" : "btn-container"}
                   style={(isExpired && {
                     pointerEvents: 'none',
                     filter: 'grayscale(1) opacity(.4)',
                     cursor: 'not-allowed'
                   } || {})}
              >
                <img src="/Red-Large-Square-PayNow.png" className="pay-now" onClick={payNow} />
              </div>
            </div>
          </Tooltip>
        </div>
        : ""
      }

      {quote.QuoteStatus === Enums.QuoteStatus.Approved ?
        <div className={mobileView ? "row" : ""}>
          <div className={mobileView ? "left" : "btn-container"}>
            <Button
              onClick={() => acceptDeclineQuoteClick(Enums.QuoteStatus.Declined)}
              text="Decline"
              extraClasses="small-margin red-light"
            />
          </div>
          <div className={mobileView ? "right" : "btn-container"}>
            <Button
              onClick={() => acceptDeclineQuoteClick(Enums.QuoteStatus.Accepted)}
              text="Accept"
              extraClasses="small-margin green"
            />
          </div>

        </div>

        : ''
      }

      <style jsx>{`

      .pay-now {
        margin-top: 0.5rem;
        height: 46px;
        cursor: pointer;
      }

        .quote-buttons {
          margin-bottom: 0.5rem;
          ${mobileView ? "width: 100%;" : ""}
        }

        .btn-container {
          display: inline-block;
          margin-left: 0.5rem;
        }

        .row {
          display: flex;
          justify-content: space-between;
          width: 100%;
        }
        .column {
          display: flex;
          flex-basis: 0;
          flex-direction: column;
          flex-grow: 1;
        }

        .left {
          position: relative;
          margin-left: 0.5rem;
          width: calc(50vw - 0.75rem);
        }
        .right {
          position: relative;
          margin-right: 0.5rem;
          width: calc(50vw - 0.75rem);
        }
        .center {
          width: 100%;
          text-align: center;
        }
        .center .pay-now {
          margin-bottom: -0.5rem;
        }

      `}</style>
    </div>);
  }

  if (redirect) {
    return (<>You are not authorised, redirecting you back to home</>);
  }

  if (!tenant || !quote || quoteQuery.isLoading || companyQuery.isLoading) {
    return (<>Loading...</>);
  }

  if (command === "paynow" && quote.QuoteStatus === Enums.QuoteStatus.Accepted) {
    return <>
      Redirecting...
    </>
  }

  return (
    <NoSSR>
      <LeadMessage customerID={customerID} tenantID={tenantID} page={"customerzone/viewquote"} />
      {command === "paynow" && quote.QuoteStatus !== Enums.QuoteStatus.Accepted ? <div style={{width: "784px", background: "#ffa50088", padding: "0.5rem", textAlign: "center", borderRadius: "4px", color: "black"}}>
        The quote needs to be accepted before making payment
      </div> : ""}

      <div >
        <Flex>
          <Flex>
            <h3>{quote.QuoteNumber}</h3>
          </Flex>
          <Flex ml={'auto'}>
            <Flex ml={'auto'} wrap={'wrap-reverse'}>

              <Flex ml={'auto'}>
                {!mobileView ? displayQuoteButtons() : ""}
              </Flex>


              <Flex ml={'auto'}>

                {company?.WhatsAppNumber && (
                    <MantineButton
                        bg={'white'}
                        variant="outline"
                        color="#25D366"
                        leftSection={<IconBrandWhatsapp size={16} />}
                        onClick={handleWhatsAppClick}
                        name="wa-cz-quote-followup"
                        h={40}
                        mt={8}
                        mx={8}
                        styles={{
                          root: {
                            fontWeight: 500,
                            flexShrink: 0
                          }
                        }}
                    >
                      Follow up on WhatsApp
                    </MantineButton>
                )}
                <div title="Submit question or comment" className="message-button-container"
                     onClick={() => setShowMessagePost(true)}>
                  <img src={Enums.Icon.MessageCircle} alt="icon" className="icon"/>
                </div>
                {showMessagePost ?
                    <MessagePost
                        onMessagePost={onMessagePost}
                        module={Enums.Module.Quote}
                        itemID={quote.ID}
                        customerZone={true}
                        tenantID={tenantID}
                        customerID={customerID}
                        apiUrlOverride={tenant.API}
                    /> : ''
                }
              </Flex>

              <MantineButton.Group ml={'auto'} style={{ marginTop: '0.5rem' }}>
                <MantineButton h={40} onClick={exportDocument}>Print</MantineButton>
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <MantineButton h={40} miw={50} w={50} style={{ borderLeft: '1px solid rgba(255,255,255,0.3)' }}>
                      <IconCaretDownFilled size={12} />
                    </MantineButton>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item onClick={downloadDocument}>Download</Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </MantineButton.Group>
            </Flex>
          </Flex>

        </Flex>

        <div className="row">
          <div className="contact">
            {primaryContact ?
                <>
                  <p className="contact-name">{primaryContact.FirstName + " " + primaryContact.LastName}</p>
                  <p>{quote.CustomerName}</p>
                  <p>{primaryContact.MobileNumber}</p>
                  <p>{primaryContact.EmailAddress}</p>
                </>
                : ""}
            <p>{quoteLocation ? quoteLocation.LocationDisplay : "No Location"}</p>
          </div>
        </div>

        <div className="card-container">
          <QuoteDetail quote={quote} showPrint={false} tenantID={tenantID} customerID={customerID}
                       showTotals={false} showHyperlink={false}
                       apiUrlOverride={tenant.API} mobileView={mobileView}
          />
        </div>

        <div className="card-container">
          <QuoteItems quoteItems={quote.QuoteItems} mobileView={mobileView}/>
        </div>

        <Flex
            wrap={'wrap'}
            gap={'.5em'}
            justify={'end'}
            align={'flex-start'}
        >
          {(bankName || bankAccountNumber || bankBranchCode) && (
            <div style={{marginRight: 'auto', marginTop: '0.5rem'}}>
              <p style={{margin: '3px 0 0'}}><strong>Banking Details</strong></p>
              {bankName && <p style={{margin: '3px 0 0'}}>Bank: {bankName}</p>}
              {bankAccountNumber && <p style={{margin: '3px 0 0'}}>Account Number: {bankAccountNumber}</p>}
              {bankBranchCode && <p style={{margin: '3px 0 0'}}>Branch Code: {bankBranchCode}</p>}
            </div>
          )}
          {
              itemPaymentsQuery.data?.Results && itemPaymentsQuery.data?.Results.length > 0 &&
              <div className="card-container" style={{flex: 2}}>
                <div className="card" style={{padding: '.5rem'}}>
                  <h2 className="item-heading">Quote Payments</h2>
                  <PaymentsTable paymentList={itemPaymentsQuery.data?.Results || []}/>
                </div>
              </div>
          }
          <div className="card-container">
            {
              mobileView ? <QuoteTotals quote={quote} mobileView={mobileView} currencySymbol={currencySymbol}
                                        paymentList={itemPaymentsQuery.data?.Results || []} taxPercentage={taxPercentage}/> :
                  <div className="row">
                    <div className="column"></div>
                    <div className="column-end">
                      <QuoteTotals quote={quote} mobileView={mobileView} currencySymbol={currencySymbol}
                                   paymentList={itemPaymentsQuery.data?.Results || []} taxPercentage={taxPercentage}/>
                    </div>
                  </div>
            }
          </div>
        </Flex>

      </div>


      {mobileView ? <div className="row spacer">
        <div className="floating-action-button row">
          {displayQuoteButtons()}
        </div>
      </div> : ""}

      <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions}/>

      <style jsx>{`

        .item-heading {
          margin: 0 0 1rem 0;
          padding: 0;
          font-size: 1.1rem; 
        }

        .card {
            background-color: ${colors.white};
            border-radius: ${layout.cardRadius};
            box-shadow: ${shadows.card};
            box-sizing: border-box;
            padding: 1rem 2rem;
            position: relative;
            width: 100%;
        }

        .card-container {
          margin-top: 0.5rem;
        }

        .card h3 {
            margin-top: 0;
        }

        .spacer {
          height: 4rem;
        }

        .container {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          padding: 1.5rem 3rem;
          overflow-x: hidden;
        }
          .row {
            display: flex;
          }
          .column {
            display: flex;
            flex-basis: 0;
            flex-direction: column;
            flex-grow: 1;
          }
          .column :global(.textarea-container) {
            height: 100%;
          }
          .column + .column {
            margin-left: 1.25rem;
          }
          .contact {
            width: 100%;
            padding-right: 16px;
          }

          .contact p {
            margin: 3px 0 0;
          }
          
          .contact p.contact-name {
            font-weight: bold;
          }
          .heading {
            color: ${colors.blueGrey};
            font-weight: bold;
          }
          .new-comment {
            position: relative;
          }
          .new-comment :global(.textarea-container){
            height: 5rem;
          }
          .new-comment img{
            cursor: pointer;
            position: absolute;
            right: 1rem;
            top: 1rem;
          }
          .loader {
            border-color: rgba(113, 143, 162, 0.2);
            border-left-color: ${colors.blueGrey};
            display: block;
            margin-bottom: 1rem;
            margin-top: 1rem;
          }
  
          .comment {
            background-color: ${colors.white};
            border-radius: ${layout.cardRadius};
            box-sizing: border-box;
            color: ${colors.blueGrey};
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin-top: 0.5rem;
            padding: 1.25rem 1rem;
            position: relative;
            width: 100%;
          }
          .comment-info {
            align-items: center;
            display: flex;
            margin-bottom: 4px;
          }
          .job {
            color: ${colors.bluePrimary};
            font-weight: bold;
          }
          .name {
            color: ${colors.darkPrimary};
            font-weight: bold;
          }
          .time {
            color: ${colors.blueGrey};
            font-size: 12px;
            margin-left: 1rem;
          }
          .text {
            white-space: pre-wrap;
          }
          .edit {
            margin-top: 0.2rem;
            margin-left: 1rem;
          }
          .status {
            position: absolute;
            right: 0;
            top: 0;
            width: 20rem;
          }
          .status :global(.input-container){
            background-color: ${colors.bluePrimary};
          }
          .status :global(input){
            color: ${colors.white};
          }
          .status :global(label){
            color: ${colors.white};
            opacity: 0.8;
          }
          .actions {
            display: flex;
            flex-direction: row;
          }
          .action-button {
            margin-top: -1rem;
            margin-left: 0.5rem;
          }
          .message-button-container {
            align-items: center;
            border: 1px solid ${colors.blueGreyLight};
            border-radius: ${layout.cardRadius};
            box-sizing: border-box;
            cursor: pointer;
            display: flex;
            font-weight: bold;
            height: 2.5rem;
            justify-content: space-between;
            margin-top: 0.5rem;
            padding: 0.5rem 0.5rem 0.5rem 0.5rem;
            position: relative;
            margin-right: 0.5rem;
            margin-left: 0.5rem;
            background: ${colors.white};
          }
          .button-dropdown {
            width: 80px;
          }

          .floating-action-button {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background: ${colors.white};
            box-shadow: ${shadows.cardSmallDark};           
          }
       
      `}</style>
    </NoSSR>
  );
}

export default ViewQuote;

export const Route = createFileRoute('/customerzone/viewquote')({ component: ViewQuote })
